import {
  formatUsd,
  GENERAL_MINIMUM_FOR_DIRECT_INDEXING,
  GENERAL_MINIMUM_FOR_LARGE_DIRECT_INDICES,
  HelpCenterArticleLinks,
} from "@frec-js/common";
import { useFrecRate } from "@frec-js/common-client";
import { Accordion } from "@mantine/core";
import Decimal from "decimal.js";
import { FC, useMemo } from "react";

import { TextLink } from "../../components/Link";
import ChevronDownIcon from "../../public/webstatic/svg/arrow-down-icon.svg";

const getFAQ = (frecRate: Decimal | null) => [
  {
    key: "minimum",
    title: "What is the minimum to invest in Frec Direct Indexing?",
    content: (
      <p>
        The initial investment minimum to start a Frec Direct Indexing portfolio
        for most indices is {formatUsd(GENERAL_MINIMUM_FOR_DIRECT_INDEXING)}.
        For indices that have a greater number of positions, the minimum is{" "}
        {formatUsd(GENERAL_MINIMUM_FOR_LARGE_DIRECT_INDICES)}. This is the
        minimum starting amount in order to purchase almost every stock in the
        index, resulting in more accurate tracking of the index.
      </p>
    ),
  },
  {
    key: "tradingFees",
    title: "Does Frec charge any trading fees?",
    content: (
      <p>
        Frec does not charge any trading fees for stocks and ETFs on top of the
        regulatory fees charged by financial regulators, which are typically a
        very small amount per trade.{" "}
        <TextLink
          target="_blank"
          href={HelpCenterArticleLinks["Regulatory trading fees"]}
        >
          Read more about regulatory trading fees here.
        </TextLink>{" "}
      </p>
    ),
  },
  {
    key: "ploc",
    title: "How much does Frec’s portfolio line of credit cost?",
    content: (
      <p>
        Our current interest rate for our portfolio line of credit{" "}
        {frecRate ? `is ${frecRate.times(100).toFixed(2)}%, and ` : ""}
        is subject to change based on the Effective Federal Funds Rate. Interest
        is billed monthly and is automatically added to your loan balance.
        Unlike most traditional loans, you aren’t required to make monthly
        principal payments for a portfolio line of credit. You may need to make
        payments in case of margin calls, but we can help you forecast if, when,
        and how much you’d owe.{" "}
        <TextLink target="_blank" href={HelpCenterArticleLinks["Margin calls"]}>
          Learn more about margin calls here
        </TextLink>
      </p>
    ),
  },
];

export const PricingFaq: FC = () => {
  const { data: frecRate } = useFrecRate();
  const faqContent = useMemo(() => {
    return getFAQ(frecRate);
  }, [frecRate]);
  return (
    <section className="bg-frecDarkBeige grid grid-cols-[auto_minmax(0,86rem)_auto] grid-rows-[13rem_1fr_96px_1px]">
      <div className="">{/* empty */}</div>
      <div className="dotted-v flex flex-col-reverse px-6 pb-9 lg:mx-12 lg:border-x lg:px-16">
        <h2 className="h2">FAQ</h2>
      </div>
      <div className="">{/* empty */}</div>
      {/* row */}
      <div className="">{/* empty */}</div>
      <div className="dotted-v px-6 lg:mx-12 lg:border-x lg:px-16">
        <Accordion
          chevron={<ChevronDownIcon />}
          classNames={{
            item: "border-frecBlack last:border-0",
            itemTitle: "",
            control: "px-0 cursor-pointer hover:bg-frecBlack/5",
            label: "py-6 lg:text-20",
            icon: "",
            chevron: "text-[24px] w-10! justify-center",
            panel: "",
            content: "px-0 pb-8",
          }}
        >
          {faqContent.map(({ key, title, content }) => (
            <Accordion.Item value={key} key={key}>
              <Accordion.Control>{title}</Accordion.Control>
              <Accordion.Panel>{content}</Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
      <div className="">{/* empty */}</div>
      {/* row */}
      <div className=""></div>
      <div className="dotted-v lg:mx-12 lg:border-x">{/* empty */}</div>
      <div className="">{/* empty */}</div>
      {/* row */}
      <div className="dotted-h col-span-3 border-b">{/* empty */}</div>
    </section>
  );
};
